import { useCallback } from 'react';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { SuccessorEntityPickerModal } from '@/components/entity-pickers/successor-picker/SuccessorEntityPickerModal';
import { pickReassigneeFromSuccessors } from '@/lib/successorEntityMapping';
import type { SuccessorUpdateData } from '@/pages/processes/recording/types';
import { useUndoableEditorActions } from '../actions/useUndoableEditorActions';
import { useEditorSuggestionSource } from '../../components/useEditorSuggestionSource';

interface Props {
  processId: string;
  /** The draft's start event — assignment-side suggestions anchor to it. */
  startEventStableStepId?: string;
  predecessorStepId: string;
  branchKey?: string;
  onClose: () => void;
}

/**
 * Renders the entity picker as a modal when the user clicks an amber UNCLEAR
 * end-node in the draft editor. Picking an entity materializes a fresh
 * downstream draft assignment on the predecessor for that entity.
 *
 * "Unclear" and "ending" rows are hidden: the node already IS unclear (so
 * resolving to unclear would be a no-op), and ending a branch from this
 * resolver isn't meaningful.
 */
export function DraftUnclearSuccessorPanel({
  processId,
  startEventStableStepId,
  predecessorStepId,
  branchKey,
  onClose
}: Props) {
  const { resolveUnclearWithAssignee } = useUndoableEditorActions({
    processId
  });
  const { anchorId, allowCreate } = useEditorSuggestionSource(startEventStableStepId);

  const handleSave = useCallback(
    async (data: SuccessorUpdateData) => {
      const pair = pickReassigneeFromSuccessors(data);
      if (!pair) return;
      try {
        await resolveUnclearWithAssignee({
          predecessorStepId,
          branchKey,
          assignedTo: pair.assigneeId,
          assignedToModel: pair.assigneeModel,
          assignedToType: pair.type
        });
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
      onClose();
    },
    [resolveUnclearWithAssignee, predecessorStepId, branchKey, onClose]
  );

  return (
    <SuccessorEntityPickerModal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      onSave={handleSave}
      processId={processId}
      recordingId={anchorId}
      draftProcessId={processId}
      allowCreate={allowCreate}
      hideUnclear
      hideNone
    />
  );
}
